import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import "../common/commonStyles.css";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const CustomerMonthlySalesReport = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [customerId, setCustomerId] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInputValue, setCustomerInputValue] = useState("");
  const [monthlyReport, setMonthlyReport] = useState([]);
  const [totals, setTotals] = useState({
    Jan: 0, Feb: 0, Mar: 0, Apr: 0, May: 0, Jun: 0,
    Jul: 0, Aug: 0, Sep: 0, Oct: 0, Nov: 0, Dec: 0,
    YearTotal: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem('token');

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 10; i--) {
      years.push(i);
    }
    return years;
  };

  const fetchCustomerNameOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setCustomerOptions([]);
      return;
    }

    const formData = new FormData();
    formData.append("pattern", inputValue);
    formData.append("platform", "web");
    formData.append("authorization", accessToken);

    const apiSalesRapUrl = `${config.API_URL}/ws-get-customer-list`;
    try {
      const response = await fetch(apiSalesRapUrl, {
        method: "POST",
        headers: {},
        body: formData,
      });

      const data = await response.json();
      const formattedOptions = Array.isArray(data.customer_details)
        ? data.customer_details.map((item) => ({
          label: item.name,
          value: item.id,
        }))
        : [];

      setCustomerOptions(formattedOptions);
    } catch (error) {
    }
  };

  const handleCustomerInputChange = (event, newInputValue) => {
    setCustomerInputValue(newInputValue);
    if (newInputValue && newInputValue.length >= 3) {
      fetchCustomerNameOptions(newInputValue);
    } else {
      setCustomerOptions([]);
    }
  };

  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue);
    setCustomerId(newValue ? newValue.value : "");
    setPage(0);
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const plain = {
        draw: 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        customer_id: customerId || '',
        year: year.toString()
      };
      if (useEncrypt) {
        const ep = encryptPayload(plain);
        if (ep) {
          formData.append('encrypted_payload', ep);
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }

      const response = await fetch(`${config.API_URL}/customerMonthlySales_Report`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const raw = await response.json();
      const json = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      if (json && json.data && Array.isArray(json.data)) {
        setMonthlyReport(json.data);
        setTotalCount(json.recordsTotal || json.data.length);

        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const newTotals = { YearTotal: 0 };

        months.forEach(month => {
          const monthTotal = json.data.reduce((sum, row) => {
            return sum + parseFloat(row[month] || 0);
          }, 0);
          newTotals[month] = monthTotal;
          newTotals.YearTotal += monthTotal;
        });

        setTotals(newTotals);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, customerId, year]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleYearChange = (event, newValue) => {
    if (newValue != null) {
      setYear(newValue);
      setPage(0);
    }
  };

  const fetchAllDataForExport = async () => {
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const plain = {
        draw: "1",
        start: "0",
        length: "-1",
        customer_id: customerId || '',
        year: year.toString()
      };
      if (useEncrypt) {
        const ep = encryptPayload(plain);
        if (ep) {
          formData.append('encrypted_payload', ep);
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }

      const response = await fetch(`${config.API_URL}/customerMonthlySales_Report`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      return data.data || [];
    } catch (error) {
      return [];
    }
  };

  const fetchAllDataPaginated = async () => {
    let allData = [];
    let start = 0;
    const length = 1000;
    let hasMore = true;
    let attempts = 0;
    const maxAttempts = 100;

    while (hasMore && attempts < maxAttempts) {
      attempts++;

      try {
        const settings = await getCompanySettings();
        const useEncrypt = resolveCustomerApiEncrypt(settings);
        const formData = new FormData();
        const plain = {
          draw: attempts.toString(),
          start: start.toString(),
          length: length.toString(),
          customer_id: customerId || '',
          year: year.toString()
        };
        if (useEncrypt) {
          const ep = encryptPayload(plain);
          if (ep) {
            formData.append('encrypted_payload', ep);
          } else {
            Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
          }
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }

        const response = await fetch(`${config.API_URL}/customerMonthlySales_Report`, {
          method: "POST",
          headers: {
            authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        const raw = await response.json();
        const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
        const pageData = data.data || [];

        allData = allData.concat(pageData);

        if (pageData.length < length || pageData.length === 0) {
          hasMore = false;
        } else {
          start += length;
        }
      } catch (error) {
        hasMore = false;
      }
    }

    return allData;
  };

  const exportToCSV = (data) => {
    const headers = [
      "Customer Name",
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      "Year Total"
    ];

    let csvContent = headers.join(",") + "\n";

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let monthTotals = {};
    let grandTotal = 0;

    months.forEach(month => {
      monthTotals[month] = 0;
    });

    data.forEach(row => {
      const rowData = [
        `"${(row.customer_info || "").replace(/"/g, '""')}"`,
        parseFloat(row.Jan || 0).toFixed(2),
        parseFloat(row.Feb || 0).toFixed(2),
        parseFloat(row.Mar || 0).toFixed(2),
        parseFloat(row.Apr || 0).toFixed(2),
        parseFloat(row.May || 0).toFixed(2),
        parseFloat(row.Jun || 0).toFixed(2),
        parseFloat(row.Jul || 0).toFixed(2),
        parseFloat(row.Aug || 0).toFixed(2),
        parseFloat(row.Sep || 0).toFixed(2),
        parseFloat(row.Oct || 0).toFixed(2),
        parseFloat(row.Nov || 0).toFixed(2),
        parseFloat(row.Dec || 0).toFixed(2),
        parseFloat(row.YearTotal || 0).toFixed(2)
      ];
      csvContent += rowData.join(",") + "\n";

      months.forEach(month => {
        monthTotals[month] += parseFloat(row[month] || 0);
      });
      grandTotal += parseFloat(row.YearTotal || 0);
    });

    const totalRow = [
      '"TOTAL"',
      ...months.map(month => monthTotals[month].toFixed(2)),
      grandTotal.toFixed(2)
    ];
    csvContent += totalRow.join(",") + "\n";

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);

    let filename = `customer_monthly_sales_${year}`;
    if (selectedCustomer && selectedCustomer.label) {
      filename += `_${selectedCustomer.label.replace(/[^a-zA-Z0-9]/g, '_')}`;
    }
    filename += '.csv';

    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToExcel = async (data) => {
    try {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      const excelData = data.map(row => ({
        'Customer Name': row.customer_info || '',
        'Jan': parseFloat(row.Jan || 0),
        'Feb': parseFloat(row.Feb || 0),
        'Mar': parseFloat(row.Mar || 0),
        'Apr': parseFloat(row.Apr || 0),
        'May': parseFloat(row.May || 0),
        'Jun': parseFloat(row.Jun || 0),
        'Jul': parseFloat(row.Jul || 0),
        'Aug': parseFloat(row.Aug || 0),
        'Sep': parseFloat(row.Sep || 0),
        'Oct': parseFloat(row.Oct || 0),
        'Nov': parseFloat(row.Nov || 0),
        'Dec': parseFloat(row.Dec || 0),
        'Year Total': parseFloat(row.YearTotal || 0)
      }));

      let totalsRow = { 'Customer Name': 'TOTAL' };
      let grandTotal = 0;

      months.forEach(month => {
        const monthTotal = excelData.reduce((sum, row) => sum + (row[month] || 0), 0);
        totalsRow[month] = monthTotal;
        grandTotal += monthTotal;
      });
      totalsRow['Year Total'] = grandTotal;

      excelData.push(totalsRow);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      XLSX.utils.book_append_sheet(wb, ws, `Sales ${year}`);

      let filename = `customer_monthly_sales_${year}`;
      if (selectedCustomer && selectedCustomer.label) {
        filename += `_${selectedCustomer.label.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
      filename += '.xlsx';

      XLSX.writeFile(wb, filename);

    } catch (error) {
      exportToCSV(data);
    }
  };

  const exportData = async (format = 'csv') => {
    try {
      setIsExporting(true);

      let allData = await fetchAllDataForExport();

      if (allData.length === 0 || (totalCount && allData.length < totalCount)) {
        allData = await fetchAllDataPaginated();
      }

      if (allData.length === 0) {
        return;
      }

      if (format === 'csv') {
        exportToCSV(allData);
      } else if (format === 'excel') {
        await exportToExcel(allData);
      }

    } catch (error) {
    } finally {
      setIsExporting(false);
    }
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0).toFixed(2);
  };

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <>
      <Box className="page-container">
        <Paper
          className="paper-style"
        >
          <Box sx={{ px: 2, py: 1.2 }}>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 1, gap: 2 }}>
              <Typography className="page-title">
                Customer Monthly Sales Report
              </Typography>
            </Box>

            <Grid2 container spacing={1} >
              <Grid2 size={{ xs: 12, md: 4 }} >
                <Autocomplete
                  className="input-border"
                  options={generateYearOptions()}
                  value={year}
                  onChange={handleYearChange}
                  disableClearable
                  getOptionLabel={(option) => String(option)}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <TextField {...params} size="small" />
                  )}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <Autocomplete
                  className="input-border"
                  options={customerOptions}
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  onInputChange={handleCustomerInputChange}
                  inputValue={customerInputValue}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search customer (min 3 characters)..."
                      size="small"
                      className="text-field"
                    />
                  )}
                />
              </Grid2>
              <Grid2
                size={{ xs: 12, md: 4 }}
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                alignItems={{ xs: "stretch", md: "flex-end" }}
                justifyContent="flex-end"
                gap={1}
              >
                {/* Empty label spacer */}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#212b36",
                    display: { xs: "none", md: "block" },
                  }}
                >
                  &nbsp;
                </Typography>

                {/* Submit Button */}
                <Button
                  variant="contained"
                  onClick={fetchReport}
                  className="button-variant-contained"
                  sx={{

                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  Submit
                </Button>

                {/* Export Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1,
                    width: { xs: "100%", sm: "100%", md: "auto"},
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<Iconify icon="eva:download-outline" width={20} />}
                    onClick={() => exportData("csv")}
                    disabled={isExporting}
                    className="button-variant-outlined"
                    sx={{
                      width: { xs: "100%", sm: "100%", md: "auto"},
                    }}
                  >
                    {isExporting ? "Exporting..." : "Export CSV"}
                  </Button>

                  <Button
          
                    variant="outlined"
                    startIcon={<Iconify icon="eva:download-outline" width={20} />}
                    onClick={() => exportData("excel")}
                    disabled={isExporting}
                    className="button-variant-outlined"
                    sx={{
                      width: { xs: "100%", sm: "100%", md: "auto" },
                    }}
                  >
                    {isExporting ? "Exporting..." : "Export Excel"}
                  </Button>
                </Box>
              </Grid2>
            </Grid2>
          </Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <TableContainer >
              <Table size="medium"
                className="table-cell-border"
                sx={{
                  tableLayout: "fixed"
                }}>
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell
                      sx={{

                        width: "250px",
              

                      }}
                    >
                      Customer(Ac No)
                    </TableCell>
                    {months.map((month) => (
                      <TableCell
                        key={month}
                        sx={{

                          width: "100px",
                          minWidth: "100px",
                          textAlign: "right",
                          color: "#212b36",
                        }}
                      >
                        {month}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f7fa",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        letterSpacing: 0.5,
                        py: 1,
                        borderBottomColor: "rgba(145, 158, 171, 0.16)",
                        width: "120px",
                        minWidth: "120px",
                        textAlign: "right",
                        color: "#212b36",
                      }}
                    >
                      Year Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthlyReport.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ color: "#637381" }}>
                          No data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    monthlyReport.map((row, index) => (
                      <TableRow
                        key={index}
                        className="table-cell-style"
                        sx={{
                    
                          "&:last-child td": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
         
                            borderBottomColor: "rgba(145, 158, 171, 0.16)",
                          }}
                        >
                          {row.customer_info || ""}
                        </TableCell>
                        {months.map((month) => (
                          <TableCell
                            key={month}
                            sx={{
                       
                              textAlign: "right",
                              fontWeight: 500,
                              borderBottomColor: "rgba(145, 158, 171, 0.16)",
                            }}
                          >
                            {formatNumber(row[month])}
                          </TableCell>
                        ))}
                        <TableCell
                          sx={{
                      
                            textAlign: "right",
                            fontWeight: 500,
                    
                          }}
                        >
                          {formatNumber(row.YearTotal)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#212b36",
                        py: 1,
                        backgroundColor: "#f5f7fa",
                        borderBottom: "none",
                      }}
                    >
                      Page Total
                    </TableCell>
                    {months.map((month) => (
                      <TableCell
                        key={month}
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#212b36",
                          py: 1,
                          textAlign: "right",
                          backgroundColor: "#f5f7fa",
                          borderBottom: "none",
                        }}
                      >
                        {formatNumber(totals[month])}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#212b36",
                        py: 1,
                        textAlign: "right",
                        backgroundColor: "#f5f7fa",
                        borderBottom: "none",
                      }}
                    >
                      {formatNumber(totals.YearTotal)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}

          {!loading && monthlyReport.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
                className="pagination-styles"
              />
            </Box>
          )}

        </Paper>
      </Box>

    </>
  );
};

export default CustomerMonthlySalesReport;
